import { Injectable } from '@angular/core';

import { Fixture, FixtureStage, FixtureViewType, Parameter } from '@cds/betting-offer';
import { MyBetsFixture, MyBetsMarket } from '@cds/my-bets';
import { ScoreboardSlim } from '@cds/scoreboards/v1';
import { ScoreboardFactory, isPlayer, mapLeague, mapParticipants, mapSport } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';

import { MyBetsEventModel } from '../../my-bets/models/my-bets-event.model';
import { MarketStatInfo } from '../../my-bets/models/my-bets-visualization-types';
import { EventInfo } from '../../my-bets/services/my-bets-fixture-data.service';

@Injectable({ providedIn: 'root' })
export class MyBetsFixtureFactory {
    constructor(private scoreboardFactory: ScoreboardFactory) {}

    createFromMyBetsFixture(fixture: MyBetsFixture, eventInfo: EventInfo): MyBetsEventModel {
        const event = new MyBetsEventModel();
        event.marketStatInfo = this.getMarketStatInfo(fixture);
        event.fixtureScoreboard = fixture.scoreboard as ScoreboardSlim;
        event.league = {
            parentLeagueId: fixture.parentLeagueId,
            id: eventInfo.competition.id ?? 0,
            name: eventInfo.competition.name ?? '',
        };
        event.sport = { id: eventInfo.sport.id ?? 0, name: eventInfo.sport.name ?? '', virtual: false };
        event.fixtureType = fixture.fixtureType;
        event.participants = fixture.participants.map((participant) => ({
            id: participant.participantId,
            name: participant.name.value,
            shortName: participant.name.short,
            fixtureParticipantId: participant.id,
        }));
        event.id = fixture.scoreboard?.id ?? fixture.id;
        event.name = eventInfo.fixtureName ?? '';
        event.live = fixture.stage === FixtureStage.Live;
        event.startDate = new Date(fixture.startDate);
        event.scoreboard = this.scoreboardFactory.createScoreboard(fixture.scoreboard as ScoreboardSlim, event);
        event.playerStats = fixture.playerStats ? [fixture.playerStats] : [];
        event.splitFixtureIds = [fixture.playerStats?.id];
        event.viewType = fixture.viewType;
        event.offerContext = fixture.contexts;

        return event;
    }

    createEventFromFixture(fixture: Fixture) {
        const event = new MyBetsEventModel();
        event.fixtureScoreboard = fixture.scoreboard as ScoreboardSlim;
        event.league = mapLeague(fixture.competition, fixture.tradingStage?.id || fixture.group?.parentId, fixture.group?.id);
        event.sport = mapSport(fixture);
        event.fixtureType = fixture.fixtureType;
        event.participants = mapParticipants(
            fixture.participants.filter((p) => !isPlayer(p)),
            fixture.viewType === FixtureViewType.NotSet ? FixtureViewType.European : fixture.viewType,
            fixture.hybridFixtureData,
        );
        event.id = fixture.id;
        event.name = fixture.name.short || fixture.name.value;
        event.live = fixture.stage === FixtureStage.Live;
        event.startDate = new Date(fixture.startDate);
        event.scoreboard = this.scoreboardFactory.createScoreboard(fixture.scoreboard as ScoreboardSlim, event);
        event.playerStats = fixture.playerStats;
        event.offerContext = fixture.contexts;
        event.viewType = fixture.viewType;

        return event;
    }

    private getMarketStatInfo(fixture: MyBetsFixture): MarketStatInfo[] {
        return fixture.markets?.map<MarketStatInfo>((myBetsMarket: MyBetsMarket) => ({
            marketId: myBetsMarket.id,
            marketStats: this.getMarketStats(myBetsMarket),
            playerIds: myBetsMarket.player1Id ? [myBetsMarket.player1Id] : myBetsMarket.playerIds,
            templateId: myBetsMarket.templateId,
            source: myBetsMarket.source,
            marketType: myBetsMarket.marketType,
            options: myBetsMarket.options,
        }));
    }

    private getMarketStats(myBetsMarket: MyBetsMarket): string[] {
        const { marketStats } = myBetsMarket;
        if (!marketStats) {
            return [];
        }

        const getPeriodValue = (parameters: Parameter[]) => {
            return parameters.find((p) => p.key === Tv2MarketParameters.Period)?.value;
        };
        const period = getPeriodValue(myBetsMarket.parameters);
        const isVaryingByPeriod = myBetsMarket.groupBy === Tv2MarketParameters.Period;

        if (period && isVaryingByPeriod && marketStats.some((x) => x.includes('|'))) {
            return marketStats
                .map((marketStat) => marketStat.split('|'))
                .filter((parts) => parts.length > 1 && parts[1].trim().toLowerCase() === period.toLowerCase()) // Check the second part
                .map((parts) => parts[0]);
        } else {
            return marketStats;
        }
    }
}
